// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
// utils / types
import { fToNow } from 'src/utils/format-time';
import type { INetsuiteNotification } from 'src/types/notification';
// components
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: INetsuiteNotification;
  onOpen: (notification: INetsuiteNotification) => void;
};

export default function NotificationItem({ notification, onOpen }: NotificationItemProps) {
  const isUnread = notification.status === 'unread';

  return (
    <ListItemButton
      onClick={() => onOpen(notification)}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        bgcolor: isUnread ? 'action.hover' : 'transparent',
      }}
    >
      {isUnread && (
        <Box
          sx={{
            top: 26,
            width: 8,
            height: 8,
            right: 20,
            borderRadius: '50%',
            bgcolor: 'error.main',
            position: 'absolute',
          }}
        />
      )}

      <ListItemAvatar>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            color: 'error.main',
            bgcolor: 'error.lighter',
          }}
        >
          <Iconify icon="solar:danger-triangle-bold-duotone" width={24} />
        </Stack>
      </ListItemAvatar>

      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <ListItemText
          disableTypography
          primary={
            <Typography variant="subtitle2" sx={{ pr: 2 }}>
              {notification.title}
            </Typography>
          }
          secondary={
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {notification.message}
              </Typography>
              {!!notification.recordLabel && (
                <Typography
                  variant="caption"
                  color="text.primary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Enregistrement : {notification.recordLabel}
                </Typography>
              )}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ typography: 'caption', color: 'text.disabled', mt: 0.5 }}
              >
                <span>NetSuite</span>
                <span>•</span>
                <span>{fToNow(notification.createdAt)}</span>
              </Stack>
            </>
          }
        />
      </Stack>
    </ListItemButton>
  );
}

